<template>
    <div id="new-orders-page">
        <spinner-text v-if="loading">
            {{ trans('pages.new_orders.loading') }}
        </spinner-text>
        <template v-else>
            <ui5-button
                :disabled="processing"
                @click="fetchAndProcessOrders"
            >
                <spinner v-if="processing" />
                {{ trans('pages.new_orders.fetch_process') }}
            </ui5-button>

            <div class="orders-title">
                {{ trans('pages.new_orders.manual_processing') }}
                <template v-if="visibleOrders">
                    ({{ visibleOrders.length }})
                </template>
            </div>

            <div class="selector-container">
                <ui5-select
                    @change="
                        filters.customerType =
                            $event.detail.selectedOption.value
                    "
                >
                    <ui5-option
                        :value="-1"
                        :selected="filters.customerType == -1"
                    >
                        {{ trans('pages.new_orders.all') }}
                    </ui5-option>
                    <ui5-option
                        v-for="usedTypes in usedTypes"
                        :selected="filters.customerType == usedTypes.id"
                        :value="usedTypes.id"
                        :key="'customerType.' + usedTypes.id"
                    >
                        {{ usedTypes.label }}
                    </ui5-option>
                </ui5-select>
                <ui5-select
                    @change="
                        filters.orderType = $event.detail.selectedOption.value
                    "
                >
                    <ui5-option
                        :value="-1"
                        :selected="filters.orderType == -1"
                    >
                        {{ trans('pages.new_orders.all') }}
                    </ui5-option>
                    <ui5-option
                        v-for="usedType in orderTypes || []"
                        :key="'orderType.' + usedType"
                        :selected="filters.orderType == usedType"
                        :value="usedType"
                    >
                        {{ usedType }}
                    </ui5-option>
                </ui5-select>
            </div>

            <ui5-list v-if="visibleOrders">
                <ui5-custom-li
                    v-for="order in visibleOrders"
                    :key="'manualOrder.' + order.orderId"
                    @click="goToOrder($event, order.orderId)"
                    class="order-item"
                >
                    <div class="new-order-item-container">
                        <sync-progress
                            :state="order.memoSaving"
                            :animation-speed="1"
                        />
                        <div class="column first">
                            <div class="new-order-item-title">
                                <i
                                    v-if="order.deliveryTime == 3"
                                    class="material-icons order-item-icon"
                                >
                                    fast_forward
                                </i>
                                <span>
                                    {{ order.orderId }} {{ order.reference1 }}
                                </span>
                            </div>
                            <div class="order-item-text">
                                <span>
                                    <span v-if="order.attachmentCount > 0">
                                        📎
                                    </span>
                                    {{ order.customerName }} -
                                    {{ order.orderedDate }}
                                </span>
                            </div>
                        </div>
                        <div class="column">
                            <ui5-textarea
                                :class="{
                                    'save-error': order.memoSaving == 'fail',
                                }"
                                maxlength="200"
                                ignore-scanner-detection
                                @input="
                                    order.productionMemo = $event.target.value;
                                    saveProductionMemo(order);
                                "
                                :value="order.productionMemo || ''"
                            ></ui5-textarea>
                        </div>
                        <div class="column column-with-select">
                            <div @click.stop>
                                <ui5-label :for="'paintshop-' + order.orderId">
                                    {{ trans('pages.edit_product.paintshop') }}
                                </ui5-label>
                                <ui5-select
                                    :id="'paintshop-' + order.orderId"
                                    @change="
                                        order.paintshop =
                                            $event.detail.selectedOption.value;
                                        updatePaintshopForOrder(order);
                                    "
                                >
                                    <ui5-option :value="null">
                                        {{
                                            trans(
                                                'pages.edit_product.select_paintshop'
                                            )
                                        }}
                                    </ui5-option>
                                    <ui5-option
                                        v-for="option in paintshopOptions"
                                        :key="'paintshopOption.' + option.value"
                                        :selected="
                                            order.paintshop == option.value
                                        "
                                        :value="option.value"
                                    >
                                        {{ option.label }}
                                    </ui5-option>
                                </ui5-select>
                            </div>
                        </div>
                        <div class="column last">
                            <div
                                v-for="additionalText in getAdditionalTexts(
                                    order
                                )"
                                :class="additionalText.cssClass"
                                :key="`additionalText.${order.orderId}.${additionalText.text}`"
                            >
                                {{ additionalText.text }}
                            </div>
                        </div>
                    </div>
                </ui5-custom-li>
            </ui5-list>

            <div class="auto-processsed-container">
                <div>
                    {{ trans('pages.new_orders.processed') }}
                    <template v-if="autoProcessedOrders">
                        ({{ autoProcessedOrders.length }})
                    </template>
                </div>
                <ui5-button @click="clearAutoProcessedOrders">
                    <spinner v-if="clearing" />
                    {{ trans('pages.new_orders.clear_notifications') }}
                </ui5-button>
                <ui5-list v-if="autoProcessedOrders">
                    <ui5-li
                        v-for="order in autoProcessedOrders"
                        @click="goToOrder(order.orderId)"
                        :key="'autoProcessedOrder.' + order.orderId"
                        :description="
                            order.customerName + ' - ' + order.insertDate
                        "
                    >
                        {{ order.orderId }}
                        {{ order.reference1 }}
                    </ui5-li>
                </ui5-list>
            </div>
        </template>
    </div>
</template>
<script>
import orderApi from '../api/order';
import paintshopApi from '../api/paintshop';
import productApi from '../api/product';
import Spinner from '../components/loader/Spinner';
import SpinnerText from '../components/loader/SpinnerText';
import SyncProgress from '../components/loader/SyncProgress.vue';
import { showInfoMessage, showErrorMessage } from '../dialogs';
import _ from 'lodash';
import localization from '../mixins/localization';

export default {
    components: { SyncProgress, Spinner, SpinnerText },
    computed: {
        usedTypes() {
            if (!this.customerTypes || !this.orders) {
                return [];
            }
            const usedIds = _.uniq(this.orders.map((x) => x.customerType));
            return this.customerTypes.filter((type) => {
                return usedIds.includes(type.ID);
            });
        },
        visibleOrders() {
            if (!this.orders) {
                return [];
            }
            return this.orders.filter((order) => {
                return (
                    (this.filters.customerType == -1 ||
                        this.filters.customerType == order.customerType) &&
                    (this.filters.orderType == -1 ||
                        this.filters.orderType == order.type)
                );
            });
        },
        filters: {
            get() {
                return this.$store.getters['newOrders/getFilters'];
            },
            set(value) {
                this.$store.commit('newOrders/setFilters', value, {
                    root: true,
                });
            },
        },
    },
    data() {
        return {
            loading: false,
            processing: false,
            clearing: false,
            orders: null,
            customerTypes: null,
            orderTypes: null,
            autoProcessedOrders: null,
            paintshopOptions: [],
        };
    },
    mixins: [localization],
    methods: {
        async saveProductionMemoInner(order) {
            order.memoSaving = 'running';
            try {
                const result = await orderApi.updateProperty(
                    order.id,
                    'productionMemo',
                    order.productionMemo
                );
                order.memoSaving = 'done';
                window.setTimeout(() => {
                    order.memoSaving = '';
                }, 3000);
            } catch (error) {
                console.error(error);
                order.memoSaving = 'fail';
                showErrorMessage(
                    this.trans('pages.new_orders.error_save_memo', {
                        orderId: order.id,
                    })
                );
            }
        },
        getAdditionalTexts(order) {
            const texts = [];
            if (order.deliveryTime === 3) {
                texts.push({
                    text: this.trans('pages.new_orders.express_delivery'),
                    cssClass: 'order-state-error',
                });
            } else if (order.deliveryTime == 2) {
                texts.push({
                    text: this.trans('pages.new_orders.shortened_delivery'),
                    cssClass: '',
                });
            }

            if (order.waitForOrderConfirmation) {
                texts.push({
                    text: this.trans('pages.new_orders.waiting_confirmation'),
                    cssClass: 'order-state-warning',
                });
            }
            return texts;
        },
        goToOrder($event, orderId) {
            const targetElement = $event.target;
            if (
                targetElement.name == 'ui5-textarea' ||
                targetElement.closest('ui5-textarea')
            ) {
                return;
            }

            this.$store.commit('general/setOrderId', orderId, { root: true });

            productApi.productsForOrderV2(orderId).then((response) => {
                const products =
                    response.data &&
                    response.data.data &&
                    response.data.data.products
                        ? response.data.data.products
                        : [];
                if (products.length == 0) {
                    showErrorMessage(
                        this.trans('pages.new_orders.error_no_product')
                    );
                } else {
                    const product = products[0];
                    this.$router.push(
                        '/editproduct/?orderId=' +
                            orderId +
                            '&productId=' +
                            product.productId +
                            '&productState=edit-product'
                    );
                }
            });
        },
        fetchAndProcessOrders() {
            this.processing = true;
            orderApi
                .fetchAndProcessOrders()
                .then((response) => {
                    const data = response.data;
                    const autoProcessedOrders =
                        data.data.autoProcessedOrders || 0;
                    const manualOrders = data.data.manualOrders || 0;
                    const totalOrders = autoProcessedOrders + manualOrders;

                    if (totalOrders > 0) {
                        showInfoMessage(
                            this.trans(
                                'pages.new_orders.info_orders_processed',
                                {
                                    autoProcessed: autoProcessedOrders,
                                    manualOrders: manualOrders,
                                }
                            ),
                            () => {
                                this.populateWinfaktOrders();
                            }
                        );
                    } else {
                        showInfoMessage(
                            this.trans('pages.new_orders.info_no_new_orders')
                        );
                    }
                })
                .catch((error) => {
                    if (error.response && error.response.status === 423) {
                        return showErrorMessage(
                            this.trans('pages.new_orders.error_orders_busy'),
                            function () {
                                location.reload();
                            }
                        );
                    }

                    showErrorMessage(
                        this.trans('pages.new_orders.error_fetch_orders')
                    );
                })
                .finally(() => {
                    this.processing = false;
                });
        },
        populateWinfaktOrders() {
            this.loading = true;
            Promise.all([
                orderApi.manualOrdersForWinfakt().then((response) => {
                    const data = response.data.data;
                    this.orders = data.orders.map((x) => ({
                        ...x,
                        memoSaving: '',
                    }));
                    this.customerTypes = data.customerTypes;
                }),
                orderApi.autoProcessedOrders().then((response) => {
                    this.autoProcessedOrders = response.data.data;
                }),
            ]).then(() => {
                this.loading = false;
            });
        },
        clearAutoProcessedOrders() {
            this.clearing = true;
            orderApi
                .clearAutoProcessedOrders()
                .then(() => {
                    showInfoMessage(
                        this.trans(
                            'pages.new_orders.info_notifications_cleared'
                        )
                    );
                    this.populateWinfaktOrders();
                })
                .finally(() => {
                    this.clearing = false;
                });
        },
        updatePaintshopForOrder(order) {
            orderApi.setPaintshop(order.orderId, order.paintshop).catch(() => {
                showErrorMessage(
                    this.trans('pages.edit_product.error_updating_paintshop')
                );
            });
        },
        fetchPaintshopOptions() {
            paintshopApi.fetchOptions().then((response) => {
                this.paintshopOptions = response.data.data || [];
            });
        },
    },
    mounted() {
        this.saveProductionMemo = _.debounce(this.saveProductionMemoInner, 800);
        this.fetchPaintshopOptions();

        orderApi.fetchAllOrderTypes().then((request) => {
            this.orderTypes = request.data.data;
        });

        this.populateWinfaktOrders();
    },
};
</script>
